import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Map } from 'immutable';
import Noty from 'noty';
import piction from '../../utils/piction';
import UMODetailViewBody from './UMODetailViewBody';
import Loader from '../ui/Loader';
import { WorkspaceTag, AlternatesTag } from '../../utils/searchUtils';
import { SESSION_EXPIRED_MESSAGE } from '../../constants/sessionStateTypes';
import { changeDataLanguage, sessionExpired } from '../../actions/session';
import appConfig from '../../appConfig';
import { addError } from '../../actions/messageDialog';
import { toggleFullImageView, toggleUMODetailView } from '../../actions/mainUI';
import { retrieveAlternates, retrieveUMOs } from '../../actions/umoGallery';

const mapStateToProps = (state) => ({
  umoGallery: state.umoGallery,
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  sessionExpired: (err) => dispatch(sessionExpired(err)),
  addError: (err) => dispatch(addError(err)),
  toggleFullImageViewDispatch: (isShown, umoData) =>
    dispatch(toggleFullImageView(isShown, umoData)),
  changeDataLanguage: (lang, umoid) => {
    dispatch(changeDataLanguage(lang));
    dispatch(retrieveUMOs());
    dispatch(toggleUMODetailView(umoid));
  },
});

class UMODetailView extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isLoading: true,
      primaryID: null,
      umo: null,
      alternates: null,
      hasChanged: false,
      isWorkspace: false,
      workspaceID: null,
      isError: false,
    };

    this.requestPromise = null;
    this.getUMOData = this.getUMOData.bind(this);
    this.onFullImageToggle = this.onFullImageToggle.bind(this);
    this.onAltClick = this.onAltClick.bind(this);
    this.onPrimarySelect = this.onPrimarySelect.bind(this);
    this.onLanguageChange = this.onLanguageChange.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  componentDidMount() {
    // Derive the view type from tagList
    const tagList = this.props.umoGallery.getIn(['searchParams', 'tagList']);
    if (tagList && tagList.length === 1) {
      const tag = tagList[0];
      if (tag instanceof WorkspaceTag) {
        this.setState({
          isWorkspace: true,
          workspaceID: tag.value,
        });
      } else if (tag instanceof AlternatesTag) {
        this.primaryUMOID = tag.value;
      }
    }
    this.getUMOData();
  }

  componentWillUnmount() {
    if (this.requestPromise) {
      this.requestPromise.cancel();
    }
    this.refreshGalleryIfChanged();
  }

  refreshGalleryIfChanged() {
    if (this.state.hasChanged) {
      if (this.primaryUMOID) {
        this.props.dispatch(
          retrieveAlternates(
            this.state.primaryID,
            this.props.intl.formatMessage({ id: 'umoGridItem.alternateItemTagLabel' }),
            this.props.intl.formatMessage({ id: 'umoGridItem.alternateItemTagDescription' })
          )
        );
      } else {
        this.props.dispatch(retrieveUMOs());
      }
    }
  }

  getUMOData() {
    const callData = appConfig.alternateReps
      ? {
          zoomify: 'true',
          METADATA: 'VIEW',
          CHECK_TAG_IS_EDIT: 'TRUE',
          CHECK_ALTERNATE_REPS: 'TRUE',
          ALTERNATE_COLLECTION: appConfig.alternateReps[1],
          ALTERNATE_METATAG: appConfig.alternateReps[0],
          ALTERNATE_EXCLUDE_RELATIONAL: appConfig.alternateExcRelational,
        }
      : {
          zoomify: 'true',
          ENABLE_ALTERNATE: 'true',
          METADATA: 'VIEW',
          CHECK_TAG_IS_EDIT: 'TRUE',
        };

    this.requestPromise = piction
      .getUMOs(`UMO_ID:"${this.props.umoID}"`, callData)
      .then((res) => {
        if (res.umoList.all[0]) {
          const umo = res.umoList.all.find((obj) => obj.id === this.props.umoID);
          if (umo.data.re && umo.data.re[0] > 1) {
            const representativesID = this.primaryUMOID || this.props.umoID;
            callData.MAXROWS = appConfig.alternateMaxRows || '50';
            this.requestPromise = piction
              .getUMOs(`REPRESENTATIVES:${representativesID}`, callData)
              .then((res) => {
                this.setState({
                  umo,
                  primaryID: representativesID,
                  alternates: res.umoList,
                  isLoading: false,
                  isError: false,
                });

                return res;
              })
              .catch((err) => {
                this.handleError(err);
              });
          } else {
            this.setState({
              umo,
              isLoading: false,
              isError: false,
            });
          }
        } else {
          this.handleError(
            this.props.intl.formatMessage({
              id: 'umoDetailView.requestUMOError',
            })
          );
        }
        return res;
      })
      .catch((err) => {
        if (err.message && err.message.includes(SESSION_EXPIRED_MESSAGE)) {
          this.props.sessionExpired(err);
          this.props.toggleUMODetailView(false);
        } else {
          this.handleError(err);
        }
        return err;
      });
  }

  onLanguageChange(lang) {
    this.props.changeDataLanguage(lang, this.props.umoID);
    this.getUMOData();
  }

  onPrimarySelect(umo) {
    const n = new Noty({
      theme: 'bootstrap-v4',
      type: 'info',
      text: this.props.intl.formatMessage({
        id: 'umoDetailView.setAsPrimaryWarningMessage',
      }),
      layout: 'center',
      buttons: [
        Noty.button(
          'Change',
          'btn btn-primary',
          () => {
            const callArgs = {
              PTR_ID: umo,
              UMO_ID: this.state.primaryID,
              SWAP_METADATA: 'FALSE',
            };
            this.setState({ isLoading: true, hasChanged: true });
            this.requestPromise = piction
              .request('set_master', callArgs)
              .then((res) => {
                this.setState({ primaryID: umo, isLoading: false });
              })
              .catch((err) => {
                if (
                  err.message &&
                  err.message.includes(SESSION_EXPIRED_MESSAGE)
                ) {
                  this.props.sessionExpired(err);
                }
                this.setState({ isLoading: false, hasChanged: false });
              });
            n.close();
          },
          { id: 'button1', 'data-status': 'ok' }
        ),

        Noty.button('Cancel', 'btn btn-error', () => {
          n.close();
        }),
      ],
    });
    n.show();
  }

  onFullImageToggle() {
    const { umo } = this.state;
    let zoomifyURL = umo.data.z[0];
    // Gross hardcoding of 3D template. Revert after demo.
    if (umo.id === '7092422') {
      zoomifyURL += '&override_template=SYSTEM.TIFF3D&override_suffix=3D';
    }
    const umoData = {
      umo,
      fullSrc:
        umo.type === 'PHOTO'
          ? umo.webQuality.url
          : umo.originalList.all[0]._data.$.h,
      type: umo.type,
      zoomifyURL,
    };
    this.props.toggleFullImageViewDispatch(true, umoData);
  }

  onAltClick(e) {
    const newUmo = this.state.alternates.all.find(
      (obj) => obj.id === e.target.id
    );
    this.setState({ umo: newUmo, isLoading: false });
  }

  componentDidCatch(error, info) {
    this.handleError(error, info);
  }

  handleError(error, info) {
    console.log('error: ', error);
    console.log('info: ', info);
    this.setState({ isLoading: false, isError: true });
    this.props.toggleUMODetailView(false);
    this.props.addError(this.props.intl.formatMessage({ id: 'general.error' }));
  }

  render() {
    return this.state.isError ? null : (
      <div className="umo-detail-view">
        {this.state.isLoading ? (
          <Loader className="umo-detail-view__loader" />
        ) : (
          <UMODetailViewBody
            umo={this.state.umo}
            alternates={this.state.alternates}
            onAltClick={this.onAltClick}
            primaryID={this.state.primaryID}
            onPrimarySelect={this.onPrimarySelect}
            onFullImageToggle={this.onFullImageToggle}
            toggleUMODetailView={() => this.props.toggleUMODetailView(false)}
            selectedWorkspaceData={this.props.selectedWorkspaceData}
            dispatch={this.props.dispatch}
            isWorkspace={this.state.isWorkspace}
            workspaceID={this.state.workspaceID}
            selectedDataLanguage={this.props.session.get('dataLanguage')}
            languageList={this.props.session.get('languageList')}
            changeDataLanguage={this.onLanguageChange}
          />
        )}
      </div>
    );
  }
}

UMODetailView.propTypes = {
  umoGallery: PropTypes.instanceOf(Map),
  session: PropTypes.instanceOf(Map),
  umoID: PropTypes.string,
  toggleUMODetailView: PropTypes.func.isRequired,
  toggleFullImageViewDispatch: PropTypes.func,
  retrieveUMOs: PropTypes.func,
  dispatch: PropTypes.func,
  sessionExpired: PropTypes.func,
  selectedWorkspaceData: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  addError: PropTypes.func,
  changeDataLanguage: PropTypes.func,
};

export default injectIntl(
  connect(mapStateToProps, mapDispatchToProps)(UMODetailView)
);
